import type {
  FieldConfig,
  SchemaSection,
} from '@/components/form/SchemaForm/types';
import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { ValidatedField } from '@/utils/validation/schemaHelpers';
import type { z, RefinementCtx } from 'zod';

export type FieldDefUi = Omit<FieldConfig, 'name'> & {
  type: FieldConfig['type'];
};

export type FieldDef = {
  name: string | (string | number)[];
  validation: ValidatedField;
  ui: FieldDefUi;
};

export type FieldGroup = {
  key: string;
  title?: string;
  description?: string;
  divider?: boolean;
  hidden?: boolean | ((values: Record<string, unknown>) => boolean);
  fields: FieldDef[];
  refine?: (
    data: Record<string, unknown>,
    ctx: RefinementCtx,
    scope: ValidationScope,
  ) => void;
};

export type TabsFieldGroup = {
  type: 'tabs';
  key: string;
  hidden?: boolean | ((values: Record<string, unknown>) => boolean);
  activeKey?: string;
  onChange?: (key: string) => void;
  tabs: Array<{
    key: string;
    label: string;
    groups: FieldGroup[];
  }>;
};

export type FormGroup = FieldGroup | TabsFieldGroup;

export type ComposeSchemaFormOptions = {
  scope?: ValidationScope;
  requiredMessage?: string;
  extraFields?: Record<string, ValidatedField>;
  extraRefine?: (
    data: Record<string, unknown>,
    ctx: z.RefinementCtx,
    scope: ValidationScope,
  ) => void;
};

export type ComposedSchemaForm = {
  formSchema: FormSchema;
  sections: SchemaSection[];
};

export function isTabsFieldGroup(group: FormGroup): group is TabsFieldGroup {
  return 'type' in group && group.type === 'tabs';
}

export function fieldNameKey(name: string | (string | number)[]): string {
  return Array.isArray(name) ? name.map(String).join('.') : String(name);
}

export function fieldDefToFieldConfig(def: FieldDef): FieldConfig {
  return {
    ...def.ui,
    name: def.name,
    required: def.ui.required,
  };
}
